import { InboxOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Col, Flex, Form, Modal, Row, Upload } from "antd";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { createUseStyles } from "react-jss";
import useCustomerManagementApi from "../../apiHooks/useCustomerManagementApi";
import { _STYLES } from "../../common/utils/customStyles";
import { useNotificationContext } from "../../core/contexts/NotificationContextProvider";

const { mt1, textSm3, uploadLogo } = _STYLES;
const useStyles = createUseStyles({
  mt1,
  textSm3,
  uploadLogo,
});

const DocumentReUploadModal = forwardRef(
  ({ isModelOpen, toggleModal, cbOnSave }, ref) => {
    const classes = useStyles();
    const [recordId, setRecordId] = useState(null);

    const { notify } = useNotificationContext();
    const [form] = Form.useForm();

    const docFile = Form.useWatch("Document", form);

    useImperativeHandle(ref, () => ({
      async setRecordId(data) {
        setRecordId(data);
      },
    }));

    const {
      reUploadNICApi: { execute: reUploadDocument, isLoading },
    } = useCustomerManagementApi();

    const handleUpload = useCallback(
      (file) => {
        if (docFile?.fileList?.length) {
          notify({
            type: "error",
            message: "A file has already been selected",
          });
          return Upload.LIST_IGNORE;
        }
        return false;
      },
      [docFile?.fileList?.length, notify]
    );

    const handleSubmit = useCallback(
      async (data) => {
        if (!data.Document?.file) {
          notify({
            type: "error",
            message: "Please select atleast one file to continue",
          });
          return;
        }

        const formData = new FormData();

        formData.append("CustomerID", recordId);
        formData.append("NICFile", data.Document?.file);

        const res = await reUploadDocument(formData);

        if (res) {
          toggleModal();
          notify({
            message: "NIC re-uploaded successfully",
          });
          cbOnSave?.();
        }
      },
      [cbOnSave, notify, reUploadDocument, recordId, toggleModal]
    );

    useEffect(() => {
      if (!isModelOpen) {
        form.resetFields();
        setRecordId(null);
      }
    }, [isModelOpen, form]);

    return (
      <Modal
          centered
        title="Re-upload NIC"
        open={isModelOpen}
        onCancel={() => toggleModal()}
        footer={null}
      >
        <Row className={classes.mt1}>
          <Col xs={24}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              autoComplete="off"
            >
              <Form.Item label="NIC" name="Document" valuePropName="Document">
                <Upload.Dragger
                  beforeUpload={handleUpload}
                  onRemove={() => {
                    form.setFieldValue("Document", undefined);
                  }}
                >
                  <Flex vertical align="center" justify="center" gap={8}>
                    <InboxOutlined className={classes.uploadLogo} />
                    <p className={classes.textSm3}>
                      Click or drag file to this area to upload
                    </p>
                  </Flex>
                </Upload.Dragger>
              </Form.Item>
              <Flex justify="flex-end">
                <Button loading={isLoading} type="primary" htmlType="submit" icon={<UploadOutlined />}>
                  Submit
                </Button>
              </Flex>
            </Form>
          </Col>
        </Row>
      </Modal>
    );
  }
);

export default DocumentReUploadModal;
